import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { CardContainer, LabeledValue, LoadingSpinner } from "@msys/ui";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Navigate } from "react-router-dom";
import type { IntegrationId } from "../../../../clients/graphqlTypes.js";
import { BreadcrumbItem, Page } from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import type { IntegrationDashboardProps } from "../integrationRegistry.js";
import { IntegrationHealthBadge } from "./IntegrationHealthBadge.js";
import { INTEGRATIONS_BASE_PATH, integrationDashboardPath } from "../paths.js";
import { useGenericDashboardQuery } from "./GenericDashboard.generated.js";

export const GenericDashboard = ({
  integrationId,
}: IntegrationDashboardProps) => {
  const { t } = useTranslate("Integrations");
  const client = useApolloClient();
  const { data, loading } = useGenericDashboardQuery({
    client,
    variables: { integrationId: integrationId as IntegrationId },
    pollInterval: 30_000,
    fetchPolicy: "cache-and-network",
  });

  const orgIntegration = getDataOrNull(
    data?.organisationIntegration
  )?.organisationIntegration;

  const health = orgIntegration?.health;
  const events = orgIntegration?.events ?? [];

  if (loading && !orgIntegration) return <LoadingSpinner />;

  if (!orgIntegration || !health) {
    return <Navigate to={INTEGRATIONS_BASE_PATH} replace />;
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: t("Integrations"), to: INTEGRATIONS_BASE_PATH },
    {
      title: orgIntegration.integration.name,
      to: integrationDashboardPath(integrationId),
    },
  ];

  return (
    <Page title={orgIntegration.integration.name} breadcrumbs={breadcrumbs}>
      <PageContainer>
        <Stack spacing={2}>
          {/* Header with health */}
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">
              {orgIntegration.integration.name}
            </Typography>
            <IntegrationHealthBadge health={health.health} />
          </Stack>

          {/* Health card */}
          <CardContainer title={t("Health")}>
            <Stack spacing={1} sx={{ p: 1 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <IntegrationHealthBadge health={health.health} />
                {health.updatedAt && (
                  <LabeledValue label={t("Last updated")}>
                    {new Date(health.updatedAt).toLocaleString()}
                  </LabeledValue>
                )}
              </Stack>
              {health.details &&
                typeof health.details === "object" &&
                Object.entries(health.details as Record<string, unknown>).map(
                  ([key, value]) => (
                    <LabeledValue key={key} label={key}>
                      {String(value)}
                    </LabeledValue>
                  )
                )}
            </Stack>
          </CardContainer>

          {/* Events timeline */}
          <CardContainer title={t("Recent events")} itemCount={events.length}>
            <Stack spacing={0.5} sx={{ p: 1 }}>
              {events.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t("No events")}
                </Typography>
              )}
              {events.map((event, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    py: 0.5,
                    borderBottom: i < events.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  {event.__typename === "IntegrationEvent" ? (
                    <>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2">{event.title}</Typography>
                        {event.detail && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {event.detail}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0, fontFamily: "monospace" }}
                      >
                        {new Date(event.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2">
                        {event.count} {event.type}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0, fontFamily: "monospace" }}
                      >
                        {new Date(event.timeRangeFrom).toLocaleTimeString()}
                        {" – "}
                        {new Date(event.timeRangeTo).toLocaleTimeString()}
                      </Typography>
                    </>
                  )}
                </Stack>
              ))}
            </Stack>
          </CardContainer>
        </Stack>
      </PageContainer>
    </Page>
  );
};
